import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { apiGet, apiDelete } from "../../utils/apiClient";

type RunRow = {
    id: string;
    runId: string;
    job: string;
    year_month: string | null;

    tenantId: string | null;

    status: string;
    totalShops: number;
    processed: number;
    success: number;
    error: number;
    insertedTotal: number;
    startedAt: string | null;
    finishedAt: string | null;
    updatedAt: string | null;
};

function badgeClass(status: string) {
    if (status === "success") return "bg-green-100 text-green-800 border-green-200";
    if (status === "partial_error") return "bg-yellow-100 text-yellow-800 border-yellow-200";
    if (status === "error") return "bg-red-100 text-red-800 border-red-200";
    if (status === "running") return "bg-blue-100 text-blue-800 border-blue-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
}

// APIの返りが tenantId / tenant_id / tenant どっちでも拾う
function normalizeRow(raw: any): RunRow {
    const tenantRaw = raw?.tenantId ?? raw?.tenant_id ?? raw?.tenant;
    return {
        id: String(raw?.id ?? ""),
        runId: String(raw?.runId ?? raw?.run_id ?? ""),
        job: String(raw?.job ?? ""),
        year_month: raw?.year_month ?? raw?.yearMonth ?? null,

        tenantId: tenantRaw ? String(tenantRaw) : null,

        status: String(raw?.status ?? ""),
        totalShops: Number(raw?.totalShops ?? raw?.total_shops ?? 0),
        processed: Number(raw?.processed ?? 0),
        success: Number(raw?.success ?? 0),
        error: Number(raw?.error ?? 0),
        insertedTotal: Number(raw?.insertedTotal ?? raw?.inserted_total ?? 0),
        startedAt: raw?.startedAt ?? raw?.started_at ?? null,
        finishedAt: raw?.finishedAt ?? raw?.finished_at ?? null,
        updatedAt: raw?.updatedAt ?? raw?.updated_at ?? null,
    };
}

function isDeletableRow(r: RunRow) {
    // 事故防止：実行中は削除不可
    return r.status !== "running";
}

export default function SuperSchedulerRunsPage() {
    const [rows, setRows] = useState<RunRow[]>([]);
    const [loading, setLoading] = useState(true);

    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const [selected, setSelected] = useState<Record<string, boolean>>({});

    const [limit, setLimit] = useState(30);
    const [job, setJob] = useState("");
    const [status, setStatus] = useState("");

    const query = useMemo(() => {
        const p = new URLSearchParams();
        p.set("limit", String(limit));
        if (job) p.set("job", job);
        if (status) p.set("status", status);
        return p.toString();
    }, [limit, job, status]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiGet(`/api/super/scheduler-runs?${query}`);
            const json = await res.json();
            const arr = Array.isArray(json) ? json : [];
            const newRows = arr.map(normalizeRow);
            setRows(newRows);

            // 一覧更新後、存在しないIDの選択を落とす
            setSelected((prev) => {
                const next: Record<string, boolean> = {};
                const idSet = new Set(newRows.map((x) => x.id));
                for (const [k, v] of Object.entries(prev)) {
                    if (v && idSet.has(k)) next[k] = true;
                }
                return next;
            });
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => {
        load();
    }, [load]);

    // =====================
    // 単体削除
    // =====================
    const onDeleteOne = useCallback(
        async (r: RunRow) => {
            if (deletingId || bulkDeleting) return;

            if (!isDeletableRow(r)) {
                alert("実行中のジョブは削除できません");
                return;
            }

            const ok1 = window.confirm(
                `この実行履歴を削除しますか？\n\njob: ${r.job}\nrunId: ${r.runId}\ntenant: ${r.tenantId ?? "-"}`
            );
            if (!ok1) return;

            const ok2 = window.confirm(
                "scheduler_runs 本体 + run_shops サブコレクションも削除されます。\n本当に削除しますか？"
            );
            if (!ok2) return;

            try {
                setDeletingId(r.id);
                await apiDelete(`/api/super/scheduler-runs/${encodeURIComponent(r.id)}`);

                // 選択からも外す
                setSelected((prev) => {
                    const next = { ...prev };
                    delete next[r.id];
                    return next;
                });

                await load();
            } catch (e: any) {
                alert(e?.message ?? "削除に失敗しました");
            } finally {
                setDeletingId(null);
            }
        },
        [deletingId, bulkDeleting, load]
    );

    // =====================
    // チェック操作
    // =====================
    const selectedIds = useMemo(() => {
        return Object.entries(selected)
            .filter(([, v]) => v)
            .map(([k]) => k);
    }, [selected]);

    const deletableRows = useMemo(() => rows.filter(isDeletableRow), [rows]);
    const deletableIdSet = useMemo(() => new Set(deletableRows.map((r) => r.id)), [deletableRows]);

    const selectedDeletableIds = useMemo(() => {
        return selectedIds.filter((id) => deletableIdSet.has(id));
    }, [selectedIds, deletableIdSet]);

    const allDeletableSelected = useMemo(() => {
        if (deletableRows.length === 0) return false;
        return deletableRows.every((r) => !!selected[r.id]);
    }, [deletableRows, selected]);

    const toggleOne = useCallback((id: string, on: boolean) => {
        setSelected((prev) => ({ ...prev, [id]: on }));
    }, []);

    const toggleSelectAllDeletable = useCallback(
        (on: boolean) => {
            setSelected((prev) => {
                const next = { ...prev };
                for (const r of deletableRows) {
                    next[r.id] = on;
                }
                return next;
            });
        },
        [deletableRows]
    );

    const clearSelection = useCallback(() => setSelected({}), []);

    // =====================
    // 一括削除
    // =====================
    const onBulkDelete = useCallback(async () => {
        if (bulkDeleting || deletingId) return;

        const ids = selectedDeletableIds;
        if (ids.length === 0) {
            alert("削除対象が選択されていません（running は選択できません）");
            return;
        }

        const ok1 = window.confirm(`選択した ${ids.length} 件の実行履歴を削除しますか？`);
        if (!ok1) return;

        const ok2 = window.confirm(
            "各 run について scheduler_runs 本体 + run_shops サブコレクションも削除されます。\n本当に削除しますか？"
        );
        if (!ok2) return;

        setBulkDeleting(true);
        try {
            // 安全のため直列
            for (const id of ids) {
                await apiDelete(`/api/super/scheduler-runs/${encodeURIComponent(id)}`);
            }

            clearSelection();
            await load();
        } catch (e: any) {
            alert(e?.message ?? "一括削除に失敗しました（途中まで削除されている可能性があります）");
            await load();
        } finally {
            setBulkDeleting(false);
        }
    }, [bulkDeleting, deletingId, selectedDeletableIds, clearSelection, load]);

    // ✅ コンパクト共通class
    const pagePad = "p-2";
    const gap = "space-y-2";
    const label = "text-[11px] text-gray-500 leading-tight";
    const input = "border rounded px-2 py-1 text-xs leading-tight";
    const btn = "text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50";
    const btnDanger =
        "text-xs px-2 py-1 border rounded text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50";

    const th = "border px-2 py-1 text-center text-[12px] font-semibold whitespace-nowrap";
    const td = "border px-2 py-1 text-[12px] leading-tight";

    return (
        <div className={`h-[calc(100vh-64px)] overflow-y-auto ${pagePad}`}>
            <div className={`${gap} pb-16`}>
                {/* Header */}
                <div className="flex items-end justify-between gap-2">
                    <div>
                        <h1 className="text-base font-semibold">スケジュール実行履歴</h1>
                        <div className="text-[11px] text-gray-500 leading-tight">
                            直近のジョブ実行（running / success / partial_error / error）
                        </div>
                    </div>

                    <button onClick={load} className={btn} disabled={loading}>
                        再読み込み
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col">
                        <label className={label}>件数</label>
                        <select className={input} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label className={label}>job</label>
                        <input
                            className={input}
                            placeholder="例: rmsDataDownload"
                            value={job}
                            onChange={(e) => setJob(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className={label}>status</label>
                        <select className={input} value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">(all)</option>
                            <option value="running">running</option>
                            <option value="success">success</option>
                            <option value="partial_error">partial_error</option>
                            <option value="error">error</option>
                        </select>
                    </div>

                    {/* Bulk bar */}
                    <div className="ml-auto flex items-center gap-2">
                        <div className="text-[11px] text-gray-500">
                            選択: <span className="font-semibold">{selectedDeletableIds.length}</span> 件
                        </div>

                        <button
                            onClick={clearSelection}
                            disabled={bulkDeleting || deletingId !== null}
                            className={btn}
                            title="チェックを全て解除"
                        >
                            選択解除
                        </button>

                        <button
                            onClick={onBulkDelete}
                            disabled={bulkDeleting || deletingId !== null || selectedDeletableIds.length === 0}
                            className={btnDanger}
                            title="選択した run を削除（running除外）"
                        >
                            {bulkDeleting ? "一括削除中…" : "選択分を削除"}
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="rounded border overflow-x-auto bg-white">
                    <table className="w-full table-fixed border-collapse">
                        <thead className="bg-gray-100 sticky top-0 z-10">
                            <tr>
                                <th className={`${th} w-10`}>
                                    <input
                                        type="checkbox"
                                        checked={allDeletableSelected}
                                        onChange={(e) => toggleSelectAllDeletable(e.target.checked)}
                                        disabled={deletableRows.length === 0 || bulkDeleting || deletingId !== null}
                                        title="削除可能な行だけ全選択（running除外）"
                                    />
                                </th>
                                <th className={`${th} w-36`}>開始</th>
                                <th className={`${th} w-24`}>tenant</th>
                                <th className={`${th} w-44`}>job</th>
                                <th className={`${th} w-28`}>status</th>
                                <th className={`${th} w-24`}>進捗</th>
                                <th className={`${th} w-16`}>成功</th>
                                <th className={`${th} w-16`}>失敗</th>
                                <th className={`${th} w-24`}>inserted</th>
                                <th className={th}>runId</th>
                                <th className={`${th} w-28`}>操作</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td className="px-2 py-2 text-xs text-gray-500" colSpan={11}>
                                        Loading...
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td className="px-2 py-2 text-xs text-gray-500" colSpan={11}>
                                        データなし
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r) => {
                                    const started = r.startedAt ? dayjs(r.startedAt).format("YYYY/MM/DD HH:mm") : "-";
                                    const prog = `${r.processed}/${r.totalShops}`;
                                    const tenant = r.tenantId || "-";
                                    const deletable = isDeletableRow(r);
                                    const checked = !!selected[r.id];

                                    return (
                                        <tr key={r.id} className="hover:bg-gray-50">
                                            <td className={`${td} text-center`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={!deletable || bulkDeleting || deletingId !== null}
                                                    onChange={(e) => toggleOne(r.id, e.target.checked)}
                                                    title={deletable ? "選択" : "running は選択不可"}
                                                />
                                            </td>

                                            <td className={`${td} text-left`}>{started}</td>

                                            <td className={`${td} text-left truncate`} title={tenant}>
                                                {tenant}
                                            </td>

                                            <td className={`${td} text-left truncate`} title={r.job}>
                                                {r.job}
                                            </td>

                                            <td className={`${td} text-center`}>
                                                <span
                                                    className={`inline-block px-1.5 py-0.5 rounded border text-[10px] leading-none ${badgeClass(
                                                        r.status
                                                    )}`}
                                                >
                                                    {r.status}
                                                </span>
                                            </td>

                                            <td className={`${td} text-center`}>{prog}</td>
                                            <td className={`${td} text-center`}>{r.success}</td>
                                            <td className={`${td} text-center`}>{r.error}</td>
                                            <td className={`${td} text-right`}>{r.insertedTotal}</td>

                                            <td className={`${td} truncate text-left`} title={r.runId}>
                                                {r.runId}
                                            </td>

                                            <td className={`${td} text-center whitespace-nowrap`}>
                                                <Link
                                                    to={`/super/scheduler-runs/${r.id}`}
                                                    className="text-blue-600 hover:underline text-[11px] mr-2"
                                                >
                                                    詳細
                                                </Link>

                                                <button
                                                    onClick={() => onDeleteOne(r)}
                                                    disabled={!deletable || bulkDeleting || deletingId === r.id}
                                                    className={`text-[11px] px-2 py-0.5 border rounded ${!deletable
                                                        ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                                        : "text-red-600 border-red-300 hover:bg-red-50"
                                                        }`}
                                                    title={!deletable ? "running は削除不可" : "この run を削除"}
                                                >
                                                    {deletingId === r.id ? "削除中…" : "削除"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="text-[11px] text-gray-500 leading-tight">
                    ※ running のジョブは選択/削除不可（事故防止） / run_shops サブコレクションも同時削除されます
                </div>
            </div>
        </div>
    );
}
